/* eslint-disable custom/no-unsafe-deep-access */
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentCustomLink from '@Components/Results/documents/DocumentCustomLink';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import { displayConditions } from '@Components/Results/documents/DocumentExpandSections';
import { ButtonWithIcon } from '@els/els-react--button';

function DocumentButtons({ document: citation, toggleSection, getTrackingAttrs, metadata, ...props }) {
  const buttonRefs = useRef({});

  // Focus management for when sections are closed
  useEffect(() => {
    const buttonOrder = [
      'abstractExpanded',
      'indextermsExpanded',
      'claimsExpanded',
      'bibliographicInfoExpanded',
      'substancesExpanded',
      'reactionsExpanded',
      'targetsExpanded',
      'hitSubstancesExpanded',
      'hitReactionsExpanded',
      'fullText',
    ];

    // Helper function to check if a button should be rendered
    const isButtonRendered = (flag) => {
      switch (flag) {
        case 'abstractExpanded':
          return displayConditions.abstractExpanded(citation);
        case 'indextermsExpanded':
          return displayConditions.indextermsExpanded(citation);
        case 'claimsExpanded':
          return displayConditions.claimsExpanded(citation);
        case 'bibliographicInfoExpanded':
          return displayConditions.bibliographicInfoExpanded(citation);
        case 'substancesExpanded':
          return displayConditions.substancesExpanded(citation);
        case 'reactionsExpanded':
          return displayConditions.reactionsExpanded(citation);
        case 'targetsExpanded':
          return displayConditions.targetsExpanded(citation);
        case 'hitSubstancesExpanded':
          return displayConditions.hitSubstancesExpanded(citation);
        case 'hitReactionsExpanded':
          return displayConditions.hitReactionsExpanded(citation);
        case 'fullText':
          return true; // Full text is always rendered
        default:
          return false;
      }
    };

    const findNextFocusableButton = (closedFlag) => {
      const currentIndex = buttonOrder.indexOf(closedFlag);

      for (let i = currentIndex + 1; i < buttonOrder.length; i += 1) {
        const nextFlag = buttonOrder[i];
        const isRendered = isButtonRendered(nextFlag);
        const nextButton = buttonRefs.current?.[nextFlag];

        if (isRendered && nextButton) {
          return nextButton;
        }
      }
      return null;
    };

    const focusCurrentButton = (closedFlag) => {
      const currentButton = buttonRefs.current?.[closedFlag];
      if (currentButton && isButtonRendered(closedFlag)) {
        currentButton.focus();
      }
    };

    const handleFocusReturn = (event) => {
      if (event.detail && event.detail.closedSection && event.detail.focusNext) {
        const closedFlag = event.detail.closedSection;
        const nextButton = findNextFocusableButton(closedFlag);

        if (nextButton) {
          nextButton.focus();
        } else {
          focusCurrentButton(closedFlag);
        }
      }
    };

    document.addEventListener('documentSectionClosed', handleFocusReturn);
    return () => {
      document.removeEventListener('documentSectionClosed', handleFocusReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const generateButton = (flag, label, count, customAction) => {
    const trackingAttrs = getTrackingAttrs({
      'data-tracking': !props[flag],
      'data-tracking-type': 'contentview,contentlinkclick',
      'data-tracking-action': label,
    });

    const handleClick = (e) => {
      const wasExpanded = props[flag];

      if (customAction) {
        customAction(e, flag, toggleSection);
      } else {
        toggleSection(e, flag);
      }

      if (!wasExpanded) {
        // Either patent family member or regular result item
        const closestResultItem =
          e.currentTarget?.closest('.e2e-patent-family-member-item') || e.currentTarget?.closest('.e2e-results-list-item');

        setTimeout(() => {
          // Focus the expanded content section only within the current result item
          if (!closestResultItem) {
            return;
          }
          const contentElement =
            closestResultItem.querySelector(`[data-section="${flag}"]`) ||
            closestResultItem.querySelector(`#${flag}-content`);
          if (contentElement) {
            if (!contentElement.getAttribute('tabindex')) {
              contentElement.setAttribute('tabindex', '-1');
            }
            contentElement.focus();
          }
        }, 100);
      }
    };

    const contentId = `${flag}-content`;

    return (
      <ButtonWithIcon
        ref={(el) => {
          buttonRefs.current[flag] = el;
        }}
        data-e2e={`e2e-document-action-${label.toLowerCase()}`}
        type="link"
        sprite={props[flag] ? 'ChevronUp' : 'ChevronDown'}
        iconRight
        linkIconSize="xs"
        className={`e2e-action rx-no-wrap e2e-document-action-${label.toLowerCase()} u-els-font-size-base`}
        onClick={handleClick}
        aria-expanded={props[flag]}
        aria-controls={contentId}
        aria-describedby={props[flag] ? `${contentId}-description` : undefined}
        {...trackingAttrs}>
        <span>{label}</span>
        {count && ` (${count})`}
      </ButtonWithIcon>
    );
  };

  return (
    <>
      <div className="result-detail-row-btns">
        <div className="els-btn-group-left">
          {/* Abstract */}
          {displayConditions.abstractExpanded(citation) && generateButton('abstractExpanded', 'Abstract')}
          {/* Index Terms */}
          {displayConditions.indextermsExpanded(citation) && generateButton('indextermsExpanded', 'Index Terms')}
          {/* Claims */}
          {displayConditions.claimsExpanded(citation) && generateButton('claimsExpanded', 'Claims')}
          {/* Bibliographic Info */}
          {displayConditions.bibliographicInfoExpanded(citation) &&
            generateButton(
              'bibliographicInfoExpanded',
              !Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) ? 'Bibliographic Info' : 'Front Page Info'
            )}
          {/* Substances */}
          {displayConditions.substancesExpanded(citation) && generateButton('substancesExpanded', 'Substances', citation.substancesCount)}
          {/* Reactions */}
          {displayConditions.reactionsExpanded(citation) && generateButton('reactionsExpanded', 'Reactions', citation.reactionsCount)}
          {/* Targets */}
          {displayConditions.targetsExpanded(citation) && generateButton('targetsExpanded', 'Targets')}
          {/* Full Text */}
          {citation.fullTextCustom && (
            <DocumentCustomLink
              ref={(el) => {
                buttonRefs.current.fullText = el;
              }}
              citation={citation}
              getTrackingAttrs={getTrackingAttrs}
            />
          )}
          {!citation.fullTextCustom && (
            <FullText
              ref={(el) => {
                buttonRefs.current.fullText = el;
              }}
              citation={citation}
              trackingAttributes={{
                index: citation.index,
                getTrackingAttrs: getTrackingAttrs(),
              }}
              metadata={metadata}
            />
          )}
        </div>
      </div>
      {(displayConditions.hitSubstancesExpanded(citation) || displayConditions.hitReactionsExpanded(citation)) && (
        <div className="result-detail-row-btns">
          <div className="els-btn-group-left">
            {/* Hit Substances */}
            {displayConditions.hitSubstancesExpanded(citation) &&
              generateButton('hitSubstancesExpanded', 'Hit Substances', citation.substancesHitCount)}
            {/* Hit Reactions */}
            {displayConditions.hitReactionsExpanded(citation) &&
              generateButton('hitReactionsExpanded', 'Hit Reactions', citation.reactionsHitCount)}
          </div>
        </div>
      )}
    </>
  );
}

DocumentButtons.propTypes = {
  document: PropTypes.object.isRequired,
  toggleSection: PropTypes.func.isRequired,
  abstractExpanded: PropTypes.bool.isRequired,
  claimsExpanded: PropTypes.bool.isRequired,
  indextermsExpanded: PropTypes.bool.isRequired,
  bibliographicInfoExpanded: PropTypes.bool.isRequired,
  substancesExpanded: PropTypes.bool.isRequired,
  reactionsExpanded: PropTypes.bool.isRequired,
  targetsExpanded: PropTypes.bool.isRequired,
  hitSubstancesExpanded: PropTypes.bool.isRequired,
  hitReactionsExpanded: PropTypes.bool.isRequired,
  getTrackingAttrs: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
};

export default DocumentButtons;
